import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ResumeEditor = ({ 
  content, 
  onContentChange, 
  suggestions = [], 
  onAcceptSuggestion,
  onRejectSuggestion,
  isLoading = false 
}) => {
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState(null);
  const editorRef = useRef(null);

  const handleTextSelection = () => {
    const selection = window.getSelection();
    const selectedText = selection?.toString();
    
    if (selectedText && editorRef?.current?.contains(selection?.anchorNode)) {
      setSelectedText(selectedText);
      setSelectionRange({
        start: selection?.anchorOffset,
        end: selection?.focusOffset
      });
    } else {
      setSelectedText('');
      setSelectionRange(null);
    }
  };

  const handleContentChange = (e) => {
    const newContent = e?.target?.value;
    onContentChange(newContent);
  };

  const insertSuggestion = (suggestion) => {
    if (!selectionRange || !editorRef?.current) return;
    
    const textarea = editorRef?.current;
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const newContent = content?.substring(0, start) + suggestion?.improvedText + content?.substring(end);
    
    onContentChange(newContent);
    onAcceptSuggestion(suggestion?.id);
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleTextSelection);
    return () => document.removeEventListener('mouseup', handleTextSelection);
  }, []);

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Editor Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-2">
          <Icon name="FileText" size={20} className="text-primary" />
          <h3 className="font-semibold text-card-foreground">Resume Content</h3>
        </div>
        <div className="flex items-center space-x-2">
          {isLoading && (
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Loader" size={16} className="animate-spin" />
              <span>Analyzing...</span>
            </div>
          )}
          <Button variant="outline" size="sm" iconName="Download">
            Export
          </Button>
        </div>
      </div>
      {/* Editor Content */}
      <div className="relative">
        <textarea
          ref={editorRef}
          value={content}
          onChange={handleContentChange}
          placeholder="Paste your resume content here or upload a file to get started..."
          className="w-full h-96 p-6 bg-background border-0 resize-none focus:outline-none focus:ring-0 text-sm leading-relaxed font-mono"
          style={{ minHeight: '400px' }}
        />
        
        {/* Selection Tooltip */}
        {selectedText && (
          <div className="absolute top-4 right-4 bg-popover border border-border rounded-lg shadow-medium p-3 max-w-xs">
            <div className="text-xs text-muted-foreground mb-2">Selected Text:</div>
            <div className="text-sm font-medium text-popover-foreground mb-3 p-2 bg-muted rounded">
              "{selectedText}"
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              iconName="Sparkles"
              onClick={() => {/* Trigger AI suggestion for selected text */}}
            >
              Get AI Suggestion
            </Button>
          </div>
        )}
      </div>
      {/* Editor Footer */}
      <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          <span>{content?.length} characters</span>
          <span>{content?.split('\n')?.length} lines</span>
          <span>{content?.split(' ')?.filter(word => word?.length > 0)?.length} words</span>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" iconName="Undo">
            Undo
          </Button>
          <Button variant="ghost" size="sm" iconName="Redo">
            Redo
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResumeEditor;